import * as d3 from "d3";

class TreemapView extends HTMLElement {
    // Save dimensions between renders
    static dimensions = {
        width: 800,
        height: 600
    };

    connectedCallback() {
        
        // Listen for window resize to adjust size
        window.addEventListener('resize', this.handleResize.bind(this));
        
        // Listen for HTMX updates
        document.body.addEventListener('htmx:afterSwap', this.handleHtmxUpdate.bind(this));
        
        // Initial rendering
        this.renderTreemap();
    }
    
    disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize.bind(this));
        document.body.removeEventListener('htmx:afterSwap', this.handleHtmxUpdate.bind(this));
    }
    
    handleResize() {
        if (document.body.contains(this)) {
            const container = this.querySelector('#treemap-container');
            if (container) {
                const rect = container.getBoundingClientRect();
                if (rect.width > 10 && rect.height > 10) {
                    // Update our static dimensions when the size changes
                    TreemapView.dimensions = {
                        width: rect.width,
                        height: rect.height
                    };
                }
            }
            this.renderTreemap();
        }
    }
    
    handleHtmxUpdate() {
        if (document.body.contains(this)) {
            this.renderTreemap();
        }
    }
    
    renderTreemap() {
        const dataEl = this.querySelector('#treemap-data');
        if (!dataEl) return;
        
        try {
            const data = JSON.parse(dataEl.getAttribute('x-data'));
            this.createTreemap(data);
        } catch (error) {
            console.error('Failed to parse treemap data:', error);
        }
    }

    createTreemap(data) {
        const container = this.querySelector('#treemap-container');
        if (!container) return;
        
        // IMPORTANT CHANGE: Use container dimensions if available, otherwise the saved ones
        const rect = container.getBoundingClientRect();
        const width = (rect.width > 10) ? rect.width : TreemapView.dimensions.width;
        const height = (rect.height > 10) ? rect.height : TreemapView.dimensions.height;
        
        // If it's a "real" size, save it for future use
        if (width > 10 && height > 10) {
            TreemapView.dimensions = { width, height };
        }
        
        console.log('Treemap dimensions:', width, height);
        const color = d3.scaleOrdinal(d3.schemeCategory10);

        d3.select(container).selectAll("svg").remove();

        const treemap = data => d3.treemap()
                .size([width, height])
                .paddingInner(1)(
                        d3.hierarchy(data)
                                .sum(d => {
                                        if (Array.isArray(d.value)) {
                                                return d.value.reduce((acc, v) => acc + (v.Value || 0), 0);
                                        }
                                        if (typeof d.value === "number") {
                                                return d.value;
                                        }
                                        if (typeof d.value === "object" && d.value !== null && "Value" in d.value) {
                                                return d.value.Value;
                                        }
                                        return 0;
                                })
                                .sort((a, b) => {
                                        const aCount = Array.isArray(a.data.value) ? a.data.value.length : 0;
                                        const bCount = Array.isArray(b.data.value) ? b.data.value.length : 0;
                                        return bCount - aCount;
                                })
                );

        const root = treemap(data);

        const svg = d3.select(container)
                .append("svg")
                .attr("width", "100%")
                .attr("height", "100%")
                .attr("viewBox", `0 0 ${width} ${height}`)
                .attr("preserveAspectRatio", "xMidYMid meet");

        const cell = svg.selectAll("g")
                .data(root.descendants().filter(d => d.depth > 0))
                .join("g")
                .attr("transform", d => `translate(${d.x0},${d.y0})`);

        cell.append("rect")
                .attr("width", d => Math.max(width, d.x1 - d.x0))
                .attr("height", d => Math.max(height, d.y1 - d.y0))
                .attr("fill", d => color(d.data.name))
                .attr("opacity", 0.8)
                .attr("rx", 2)
                .attr("ry", 2);

        cell.append("title")
                .text(d => {
                        let txt = d.data.name || "";
                        if (Array.isArray(d.data.value) && d.data.value.length > 0) {
                                txt += "\nId: " + d.data.value.map(v => v.Name).join(", ");
                        }
                        let volume = 0;
                        if (Array.isArray(d.data.value)) {
                                volume = d.data.value.reduce((acc, v) => acc + (v.Value || 0), 0);
                        } else if (typeof d.data.value === "number") {
                                volume = d.data.value;
                        } else if (typeof d.value === "number") {
                                volume = d.value;
                        }
                        txt += `\nVolume: ${volume}`;
                        return txt;
                });

        cell.append("text")
                .attr("x", 4)
                .attr("y", 14)
                .attr("font-size", "12px")
                .attr("fill", "white")
                .text(d => (d.x1 - d.x0 > 40 && d.y1 - d.y0 > 18) ? d.data.name : "");

        cell.append("text")
                .attr("x", 4)
                .attr("y", 28)
                .attr("font-size", "10px")
                .attr("fill", "white")
                .selectAll("tspan")
                .data(d => {
                        let lines = [];
                        if (Array.isArray(d.data.value) && d.data.value.length > 0) {
                                const names = d.data.value.map(v => v.Name);
                                for (let i = 0; i < names.length; i += 3) {
                                        lines.push(names.slice(i, i + 3).join(", "));
                                }
                        }
                        let volume = 0;
                        if (Array.isArray(d.data.value)) {
                                volume = d.data.value.reduce((acc, v) => acc + (v.Value || 0), 0);
                        } else if (typeof d.data.value === "number") {
                                volume = d.data.value;
                        } else if (typeof d.value === "number") {
                                volume = d.value;
                        }
                        lines.push(`Volume: ${volume}`);
                        return lines;
                })
                .join("tspan")
                .attr("x", 4)
                .attr("dy", (d, i, nodes) => {
                        const parent = nodes[i].parentNode.__data__;
                        return (parent.x1 - parent.x0 > 40 && parent.y1 - parent.y0 > 30) ? (i === 0 ? 0 : "1.2em") : 0;
                })
                .text((d, i, nodes) => {
                        const parent = nodes[i].parentNode.__data__;
                        return (parent.x1 - parent.x0 > 40 && parent.y1 - parent.y0 > 30) ? (i === 0 ? `Id: ${d}` : d) : "";
                });
}
}
if (!customElements.get('treemap-view')) {
        customElements.define('treemap-view', TreemapView);
}